import type { ChannelListItem } from '@/models';

const norm = (value?: string) => value?.trim().toUpperCase();

/** Buying metrics where delivered volume = budget ÷ sell rate (not benchmark-derived). */
export function isUnitVolumeBuyingMetric(buyingMetric?: string): boolean {
  const metric = norm(buyingMetric);
  return metric === 'CPV' || metric === 'CPCV' || metric === 'CPC';
}

export function shouldShowEstimatedViews(buyingMetric?: string, kpi?: string): boolean {
  const metric = norm(buyingMetric);
  if (metric === 'CPV' || metric === 'CPCV') return true;
  if (metric === 'CPM') return kpi?.trim().toLowerCase() === 'vtr';
  return false;
}

export function shouldShowEstimatedClicks(buyingMetric?: string, kpi?: string): boolean {
  const metric = norm(buyingMetric);
  if (metric === 'CPC') return true;
  if (metric === 'CPM') return kpi?.trim().toLowerCase() === 'ctr';
  return false;
}

export function shouldShowEstimatedListens(buyingMetric?: string, kpi?: string): boolean {
  if (norm(buyingMetric) === 'CPM') return kpi?.trim().toLowerCase() === 'ltr';
  return false;
}

export function shouldShowImpressions(buyingMetric?: string): boolean {
  return norm(buyingMetric) === 'CPM';
}

type LineQuantityFields = Pick<
  ChannelListItem,
  'impressions' | 'views' | 'clicks' | 'buyingMetric' | 'budget' | 'sellRate'
>;

/** Delivered volume for one line item: impressions (CPM), views (CPV/CPCV), or clicks (CPC). */
export function resolveLineQuantity(row: LineQuantityFields): number {
  const metric = norm(row.buyingMetric);
  if (metric === 'CPV' || metric === 'CPCV') return resolveDeliveredViews(row) ?? 0;
  if (metric === 'CPC') return resolveDeliveredClicks(row) ?? 0;
  if (metric === 'CPM') return row.impressions ?? 0;
  return 0;
}

export function getMediaPlanTotalQuantity(
  lineItems: LineQuantityFields[] | undefined,
  fallbackImpressions?: number
): number | undefined {
  const lines = lineItems ?? [];
  if (lines.length === 0) return fallbackImpressions;

  const total = lines.reduce((sum, line) => sum + resolveLineQuantity(line), 0);
  if (total > 0) return total;
  return fallbackImpressions;
}

/** Client-side fallback when stored channel metrics predate CPV/CPC logic. */
export function resolveDeliveredViews(row: Pick<ChannelListItem, 'views' | 'buyingMetric' | 'budget' | 'sellRate'>): number | undefined {
  if (row.views != null && row.views > 0) return row.views;
  const metric = norm(row.buyingMetric);
  if ((metric === 'CPV' || metric === 'CPCV') && row.budget && row.sellRate && row.sellRate > 0) {
    return Math.round(row.budget / row.sellRate);
  }
  return undefined;
}

export function resolveDeliveredClicks(row: Pick<ChannelListItem, 'clicks' | 'buyingMetric' | 'budget' | 'sellRate'>): number | undefined {
  if (row.clicks != null && row.clicks > 0) return row.clicks;
  const metric = norm(row.buyingMetric);
  if (metric === 'CPC' && row.budget && row.sellRate && row.sellRate > 0) {
    return Math.round(row.budget / row.sellRate);
  }
  return undefined;
}
